import { useEffect, useRef, useState } from "react"

import { useNavigate } from "react-router-dom"

import { ColumnDef, ColumnFiltersState, flexRender, getCoreRowModel, getFilteredRowModel, getSortedRowModel, SortingState, useReactTable, VisibilityState } from "@tanstack/react-table"
import { CollectionItem } from "@vertesia/common"
import { FolderClosed, Loader2, Search, MoreHorizontal } from "lucide-react"

import { ColumnFilter } from "@components/datatables/filters/ColumnFilter"
import { DeleteCollectionModal } from "@components/modals/DeleteCollectionModal"

import { useIntersectionObserver } from "@hooks/use-intersection-observer"

import { Button } from "@ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@ui/dropdown-menu"
import { Skeleton } from "@ui/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ui/table"

function columns(handleOnDelete: (collection: CollectionItem) => void): ColumnDef<CollectionItem>[] {
    const navigate = useNavigate()

    return [
        {
            accessorKey: "name",
            header: ({ column }) => <ColumnFilter column={column} label="Name" hideFilter />,
            sortingFn: (rowA, rowB) => {
                const nameA = rowA.original?.name?.toLowerCase() ?? ""
                const nameB = rowB.original?.name?.toLowerCase() ?? ""
                return nameA.localeCompare(nameB)
            },
            cell: ({ row }) => {
                return (
                    <div className="flex items-center gap-2">
                        {row.original?.dynamic ? <Search className="size-4 text-gray-500" /> : <FolderClosed className="size-4 text-gray-500" />}
                        <div className="underline text-blue-500 hover:cursor-pointer" onClick={() => navigate(`/collections/${row.original.id}`)}>
                            {row.getValue("name")}
                        </div>
                    </div>
                )
            },
            size: 300,
        },
        {
            accessorKey: "type",
            header: ({ column }) => <ColumnFilter column={column} label="Type" hideFilter />,
            sortingFn: (rowA, rowB) => {
                const orgA = rowA.original?.type?.name?.toLowerCase() ?? ""
                const orgB = rowB.original?.type?.name?.toLowerCase() ?? ""
                return orgA.localeCompare(orgB)
            },
            cell: ({ row }) => {
                return <div>{row.original?.dynamic ? "Dynamic Collection" : "Collection"}</div>
            },
            size: 200,
        },
        {
            id: "actions",
            enableHiding: false,
            cell: ({ row }) => {
                return (
                    <div className="flex items-center justify-center">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-gray-100 hover:cursor-pointer">
                                    <MoreHorizontal />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                <DropdownMenuItem className="cursor-pointer" onClick={() => handleOnDelete(row.original)}>
                                    Delete Collection
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )
            },
            size: 70,
        },
    ]
}

interface CollectionDataTableProps {
    data: CollectionItem[]
    loading: boolean
    hasMore?: boolean
    loadingMore?: boolean
    onRefresh: () => void
    loadMore?: () => Promise<boolean>
}
export function CollectionDataTable({ data, loading, loadMore, hasMore = false, loadingMore = false, onRefresh }: Readonly<CollectionDataTableProps>) {
    const tableContainerRef = useRef<HTMLDivElement>(null)
    const loadMoreRef = useRef<HTMLTableRowElement>(null)

    const [sorting, setSorting] = useState<SortingState>([])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})

    const [openDelete, setOpenDelete] = useState(false)
    const [collectionToDelete, setCollectionToDelete] = useState<CollectionItem | null>(null)

    const handleOnDelete = (document: CollectionItem) => {
        setCollectionToDelete(document)
    }

    const handleOnDeleteRefresh = () => {
        setCollectionToDelete(null)
        onRefresh()
    }

    const table = useReactTable({
        data,
        columns: columns(handleOnDelete),
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
        },
        enableMultiSort: true,
        columnResizeMode: "onChange",
    })

    useEffect(() => {
        setOpenDelete(!!collectionToDelete)
    }, [collectionToDelete])

    const handleScroll = () => {
        if (!tableContainerRef.current || loading || loadingMore || !hasMore || !loadMore) {
            return
        }

        const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current
        const diff = scrollHeight - scrollTop - clientHeight

        if (diff < 50) {
            loadMore()
        }
    }

    useEffect(() => {
        const tableContainer = tableContainerRef.current
        if (tableContainer) {
            tableContainer.addEventListener("scroll", handleScroll)

            return () => {
                tableContainer.removeEventListener("scroll", handleScroll)
            }
        }
    }, [loading, loadingMore, hasMore])

    useIntersectionObserver(
        loadMoreRef,
        () => {
            loadMore?.()
        },
        { deps: [data.length] }
    )

    return (
        <div className="flex flex-col h-full w-full">
            <div ref={tableContainerRef} className="flex flex-col h-full border rounded-md border-gray bg-white overflow-auto">
                <Table className="w-full">
                    <TableHeader className="sticky top-0 bg-white z-10 after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-gray-200">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} style={{ maxWidth: header.getSize() }}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={3}>
                                    <div className="flex justify-center items-center w-full h-full">
                                        <div className="w-full h-full space-y-2">
                                            {[...Array(50)].map(() => (
                                                <Skeleton key={crypto.randomUUID()} className="h-12 w-full" />
                                            ))}
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading &&
                            table.getRowModel().rows?.length > 0 &&
                            table.getRowModel().rows.map((row) => (
                                <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id} style={{ width: cell.column.getSize() }}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        {!loading && table.getRowModel().rows?.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <div className="flex justify-center items-center w-full">
                                        <p className="text-gray-500 text-center">No documents found.</p>
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {loadingMore && (
                            <TableRow>
                                <TableCell colSpan={2}>
                                    <div className="flex justify-center items-center p-4">
                                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                        {hasMore && loadMore && !loadingMore && data.length > 0 && <div ref={loadMoreRef} />}
                    </TableBody>
                </Table>
                {hasMore && data.length < 10 && <div style={{ minHeight: "100px" }}></div>}
            </div>
            <div className="flex items-center justify-end space-x-2 pt-2 text-sm text-muted-foreground">
                {loading ? (
                    "Search in Progress."
                ) : data.length === 0 ? (
                    "No results."
                ) : (
                    <>
                        {table.getFilteredRowModel().rows.length} document{table.getFilteredRowModel().rows.length <= 1 ? "" : "s"}.
                    </>
                )}
                {loadingMore && " Loading more..."}
            </div>
            {collectionToDelete && <DeleteCollectionModal collection={collectionToDelete} open={openDelete} setOpen={setOpenDelete} onDelete={handleOnDeleteRefresh} />}
        </div>
    )
}
